import { EntryType, type EntryItemType, type TreeNode } from '@morph-mapper/types';
import type { StoredSuggestion, SuggestedChange, SuggestedGraph } from '@/store';
import { fieldPath } from './open-field';
import { checkGraph, type Blocks, type GraphProblem } from './graph-change';
import { listValues, suggestionText } from './template-summary';

/** Why a suggested change can't be applied to the template as it is. */
export type ChangeProblem =
  | 'notFound'
  | 'ambiguous'
  | 'notAField'
  | 'typeNotAllowed'
  | 'notInList'
  | 'notYesNo'
  | 'noSuggestions'
  | 'noSuggestion'
  | GraphProblem['problem'];

export type ResolvedChange = {
  entryId?: string;
  /** The type the field has after the change. */
  targetType?: EntryItemType;
  /** The field's type now, when the change switches it to another type. */
  switchesFrom?: EntryItemType;
  /** What the field has now, when it keeps its type. */
  current?: string;
  /** For a suggestion: the stored suggestion the change applies. */
  suggestion?: StoredSuggestion;
  /** For a graph: the field has a graph, which the change replaces. */
  replacesGraph?: boolean;
  problem?: ChangeProblem;
  /** For a graph that can't be built: the node (and option) it is about. */
  graphProblem?: GraphProblem;
};

/** A value as one line of text: a list shows its first items. */
export const asText = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  if (Array.isArray(value)) {
    const items = value.flat(Infinity).filter((item) => item !== undefined && item !== '');
    return items.length > 3 ? `${items.slice(0, 3).join(', ')}, …` : items.join(', ') || undefined;
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

const normalise = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * The fields a name points to: the field with exactly that path, or else the
 * fields whose path ends in it. The assistant often writes just the field's own
 * name ("Inland date") instead of its whole path.
 */
const fieldsNamed = (field: string, entries: Record<string, TreeNode>): TreeNode[] => {
  const target = normalise(field);
  const paths = Object.values(entries).map((entry) => ({
    entry,
    path: normalise(fieldPath(entries, entry.id) ?? ''),
  }));

  const exact = paths.filter(({ path }) => path === target);
  const found = exact.length > 0 ? exact : paths.filter(({ path }) => path.endsWith(`.${target}`));
  return found.map(({ entry }) => entry);
};

// The words a yes/no value may come as, in the languages of the builder.
const YES = ['true', 'yes', 'ja'];
const NO = ['false', 'no', 'nee'];

/** The yes/no a value stands for, or undefined when it isn't one. */
export const asYesNo = (value: string | boolean): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  const text = normalise(value);
  return YES.includes(text) ? true : NO.includes(text) ? false : undefined;
};

/** Text as the assistant may write it: in quotes, with other spaces or case. */
const comparable = (text: string) =>
  text
    .trim()
    .replace(/^"([\s\S]*)"$/, '$1')
    .replace(/\s+/g, ' ')
    .toLowerCase();

/**
 * The texts a change can name a suggestion by: as the template mapping shows
 * it, also without the "…" of a cut value, and as the value itself.
 */
const namesOf = (value: unknown): string[] => {
  const shown = suggestionText(value);
  return [shown, shown?.replace(/…$/, ''), asText(value)]
    .filter((text): text is string => Boolean(text))
    .map(comparable);
};

/** The field's best AI suggestion that the change names. */
const suggestionWithValue = (suggestions: StoredSuggestion[], value: string) =>
  suggestions
    .filter((suggestion) => namesOf(suggestion.value).includes(comparable(value)))
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0];

/** What a field has now, in the terms of the change. */
const currentOf = (entry: TreeNode, change: SuggestedChange): string | undefined => {
  if (change.kind === 'graph') return undefined;
  if (change.kind === 'suggestion') return asText(entry.computed);
  if (entry.type === EntryType.Cell) return asText(entry.column);
  return 'value' in entry ? asText(entry.value) : undefined;
};

type Target = Pick<ResolvedChange, 'suggestion' | 'replacesGraph'> & { targetType: EntryItemType };
type TargetOrProblem = Target | Pick<ResolvedChange, 'problem' | 'graphProblem'>;

/** A fixed value: yes or no for a yes/no field, one of the options for a list field. */
const valueTarget = (entry: TreeNode, value: string | boolean): TargetOrProblem => {
  const yesNo = entry.type === EntryType.Boolean || typeof value === 'boolean';
  if (yesNo && asYesNo(value) === undefined) return { problem: 'notYesNo' };
  const values = listValues(entry);
  if (!yesNo && values && !values.includes(String(value))) return { problem: 'notInList' };
  return { targetType: yesNo ? EntryType.Boolean : EntryType.Simple };
};

/** A graph the node types can build; it replaces the field's graph, if it has one. */
const graphTarget = (entry: TreeNode, graph: SuggestedGraph, blocks: Blocks): TargetOrProblem => {
  const graphProblem = checkGraph(graph, blocks);
  if (graphProblem) return { problem: graphProblem.problem, graphProblem };
  return {
    targetType: EntryType.Graph,
    replacesGraph: entry.type === EntryType.Graph && Boolean(entry.graphId),
  };
};

/** One of the field's AI suggestions, by the text the change names it by. */
const suggestionTarget = (
  suggestions: StoredSuggestion[] | undefined,
  value: string
): TargetOrProblem => {
  if (!suggestions?.length) return { problem: 'noSuggestions' };
  const suggestion = suggestionWithValue(suggestions, value);
  if (!suggestion) return { problem: 'noSuggestion' };
  return { targetType: suggestion.type as EntryItemType, suggestion };
};

/** The type a change gives the field, and what goes with it, or why it doesn't fit. */
const targetFor = (
  change: SuggestedChange,
  entry: TreeNode,
  suggestions: StoredSuggestion[] | undefined,
  blocks: Blocks
): TargetOrProblem => {
  switch (change.kind) {
    case 'column':
      return { targetType: EntryType.Cell };
    case 'value':
      return valueTarget(entry, change.value);
    case 'graph':
      return graphTarget(entry, change.value, blocks);
    case 'suggestion':
      return suggestionTarget(suggestions, change.value);
  }
};

/**
 * Finds the field a suggested change is about, and checks that the change fits
 * it: the field's type may become the one the change needs, a list field only
 * gets one of its options, a yes/no field only yes or no, a suggestion has to
 * be one the field has, and a graph has to be one the node types can build.
 */
export const resolveChange = (
  change: SuggestedChange,
  entries: Record<string, TreeNode>,
  suggestions: Record<string, StoredSuggestion[]> = {},
  blocks: Blocks = {}
): ResolvedChange => {
  const matches = fieldsNamed(change.field, entries);
  if (matches.length === 0) return { problem: 'notFound' };
  if (matches.length > 1) return { problem: 'ambiguous' };

  const [entry] = matches;
  if (entry.type === EntryType.Map) return { entryId: entry.id, problem: 'notAField' };

  const target = targetFor(change, entry, suggestions[entry.id], blocks);
  if (!('targetType' in target)) return { entryId: entry.id, ...target };
  const { targetType, suggestion, replacesGraph } = target;

  const allowed = entry.allowedTypes ?? [];
  if (allowed.length > 0 && !allowed.includes(targetType)) {
    return { entryId: entry.id, targetType, problem: 'typeNotAllowed' };
  }

  const switches = entry.type !== targetType;
  return {
    entryId: entry.id,
    targetType,
    switchesFrom: switches ? entry.type : undefined,
    current: switches && change.kind !== 'suggestion' ? undefined : currentOf(entry, change),
    suggestion,
    replacesGraph,
  };
};
